import { Carousel, Embla } from "@mantine/carousel";
import {
  Alert,
  Container,
  createStyles,
  Flex,
  Group,
  Loader,
  Progress,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useParams } from "react-router-dom";
import { SurveyNavigationButtons } from "./_surveyNavigationButtons";
import {
  SurveyWelcome1,
  SurveyWelcome2,
  SurveyAcknowledgement,
  Genero,
  Edad,
  Comunidad,
  Etnia,
  Contrato,
  Turno,
  TiempoTrabajando,
  Area,
  ContactoConClientes,
  PuestoDeTrabajo,
  ParticipacionEnComite,
  Jefe,
  CentroDeTrabajo,
  SurveyInfoPolitics,
  ConocePoliticaDePrevencion,
  HaSidoCapacitado,
  CanalDeDenunciasExiste,
  CanalesDeDenuncias,
  ConoceProcesoDeInvestigacion,
  SurveyInfoSituations,
  PercepcionLideres,
  Tolerancia,
  Conceptos,
  SurveyInfoPersonalExperience,
  HaSidoObjetoDeAcoso,
  PreguntasAcosoDeclarado,
  PreguntasAcosoTecnico,
  Frecuencia,
  GeneroAcosador,
  PreguntasPerfilAcosador,
  AccionesAcosoDeclarado,
  BarrerasDeDenuncia,
  Costos,
  PreguntasTestigosDeclarados,
  HaSidoTestigoDeAcoso,
  PreguntasTestigosTecnicos,
  PreguntasAccionesTestigos,
  HaSidoObjetoDeAcosoAlgunaVezEnSuVida,
  SurveyInfoTratamientoDeCasos,
  PreguntasConfianzaEnLaOrganizacion,
  PreguntaSatisfaccion,
  SurveyInfoCasos,
  CasoTemplate,
} from "./ELSA_4";
import { SurveyService } from "../../services";
import { TStaffSurveyData } from "../../types";
import { useLogo } from "../../context/Logo/LogoContext";
import i18n from "../../i18n";
import { ApiWithoutToken, BASE_URL } from "../../services/axios-instance";
import { useTranslation } from "react-i18next";
import {
  transformSexualHarassmentSurveyData,
  SexualHarassmentFormValues,
} from "../../utils/sexualHarassmentDataTransform";

type Props = {
  isPreview: boolean;
  isDemoMode: boolean;
};

const useStyles = createStyles((theme) => ({
  carouselSlide: {
    overflowY: "auto",
    maxHeight: "500px",
    overflowX: "hidden",

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      maxHeight: "400px",
    },
  },
}));

export const SurveyPageSexualHarassment = (props: Props) => {
  const { id: mpCode } = useParams();
  const { isPreview, isDemoMode } = props;
  const [searchParams] = useSearchParams();

  const [scrollProgress, setScrollProgress] = useState(0);
  const [embla, setEmbla] = useState<Embla | null>(null);
  const [survey, setSurvey] = useState<TStaffSurveyData>(null!);
  const surveyRef = useRef(survey);
  surveyRef.current = survey;
  const [error, setError] = useState<string | null>(null);
  const [isLeader, setIsLeader] = useState<boolean>(false);
  const [esParteDeRecursosHumanos, setParteDeRecursosHumanos] =
    useState<boolean>(false);
  const [haSidoObjetoDeAcoso, setHaSidoObjetoDeAcoso] =
    useState<boolean>(false);
  const [haSidoTestigoDeAcoso, setHaSidoTestigoDeAcoso] =
    useState<boolean>(false);
  const [totalSlides, setTotalSlides] = useState<number>(0);
  const [encuestaVencida, setEncuestaVencida] = useState(false);
  const [encuestaTodaviaNoHaSidoLanzada, setEncuestaTodaviaNoHaSidoLanzada] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const { updateLogo } = useLogo();

  const navigate = useNavigate();

  const handleScroll = useCallback(() => {
    if (!embla) return;
    const progress = Math.max(0, Math.min(1, embla.scrollProgress()));
    setScrollProgress(progress * 100);
  }, [embla, setScrollProgress]);

  useEffect(() => {
    if (embla) {
      embla.on("scroll", handleScroll);
      handleScroll();
    }
  }, [handleScroll, embla]);

  const { classes } = useStyles();

  const validateField = (value: any, fieldName?: string) => {
    // Skip validation for IL_004_area when survey ID is 237 CELEPSA
    if (fieldName === 'IL_004_area' && survey?.staff_survey.id === 237) {
      return null;
    }
    return !value ? t("errors.markOption") : null;
  };
  const validateCheckbox = (value: any) =>
    value.length === 0 ? t("errors.markAtLeastOneOption") : null;

  const form = useForm<SexualHarassmentFormValues>({
    initialValues: {
      IP_001_genero: "",
      IP_002_edad: "",
      IP_003_comunidad: "",
      IP_004_etnia: "",
      IL_001_contrato: "",
      IL_002_turno: "",
      IL_003_tiempoTrabajando: "",
      IL_004_area: "",
      IL_005_contactoConClientes: "",
      IL_006_puestoDeTrabajo: "",
      IL_007_participacionEnComite: "",
      IL_008_jefe: "",
      IL_009_centroDeTrabajo: "",
      SP_001_conocePoliticaDePrevencion: "",
      SP_002_haSidoCapacitado: "",
      SP_003_canalDeDenunciasExiste: "",
      SP_004_011_canalesDeDenuncias: [],
      SP_012_conoceProcesoDeInvestigacion: "",
      PL_001_percepcionLideres: "",
      TOL_001_bromasSexistas: "",
      TOL_002_bromasSobreIdentidadSexual: "",
      TOL_003_bromasSobreAparienciaFisica: "",
      TOL_004_invitarASalir: "",
      TOL_005_contactarPorRedesSociales: "",
      TOL_006_solicitarMaterialSexual: "",
      TOL_007_rozamientosIndebidos: "",
      TOL_008_tocamientosIndebidos: "",
      TOL_009_mirarConConnotacionSexual: "",
      TOL_010_invitarASalir: "",
      TOL_011_insinuarMejoresCondicionesACambioDeRelaciones: "",
      TOL_012_ameazarConSuspenderContrato: "",
      CPT_001: "",
      CPT_002: "",
      CPT_003: "",
      CPT_004: "",
      AD_001_haSidoObjetoDeAcoso: "",
      AT_001_comentariosConContenidoSexual: "",
      AT_002_comentariosSobreOrientacionSexual: "",
      AT_003_comentariosSobreAparienciaFisica: "",
      AT_004_invitarASalir: "",
      AT_005_contactarPorRedesSociales: "",
      AT_006_solicitarMaterialSexual: "",
      AT_007_rozamientosIndebidos: "",
      AT_008_tocamientosIndebidos: "",
      AT_009_mirarConConnotacionSexual: "",
      AT_010_invitarASalir: "",
      AT_011_insinuarMejoresCondicionesACambioDeRelaciones: "",
      AT_012_amenazarConSuspenderContrato: "",
      AD_002_013: [],
      FRE_001_frecuencia: "",
      PAD_001_002: [],
      PAD_003_007: [],
      AAD_001_008: [],
      BAD_001_010: [],
      BAD_010: "",
      COS_001_005: [],
      TD_001: "",
      TD_002_013: [],
      TT_001: "",
      TT_002: "",
      TT_003: "",
      TT_004: "",
      TT_005: "",
      TT_006: "",
      TT_007: "",
      TT_008: "",
      TT_009: "",
      TT_010: "",
      TT_011: "",
      TT_012: "",
      ACT_001_008: [],
      DANIEL_001: "",
      DANIEL_002: "",
      DANIEL_003: "",
      DANIEL_004: "",
      DANIEL_005: "",
      DANIEL_006: "",
      MARIANA_001: "",
      MARIANA_002: "",
      MARIANA_003: "",
      MARIANA_004: "",
      MARIANA_005: "",
      MARIANA_006: "",
      SANDRA_001: "",
      SANDRA_002: "",
      SANDRA_003: "",
      SANDRA_004: "",
      SANDRA_005: "",
      SANDRA_006: "",
      CLAUDIA_001: "",
      CLAUDIA_002: "",
      CLAUDIA_003: "",
      CLAUDIA_004: "",
      CLAUDIA_005: "",
      CLAUDIA_006: "",
      AD_014: "",
      CON_001: "",
      CON_003: "",
      CON_004: "",
      CON_005: "",
      CON_006: "",
      CON_007: "",
      SAT_001: "",
    },

    validate: {
      IP_001_genero: (value) => validateField(value),
      IP_002_edad: (value) => validateField(value),
      IP_003_comunidad: (value) => validateField(value),
      IP_004_etnia: (value) => validateField(value),
      IL_001_contrato: (value) => validateField(value),
      IL_002_turno: (value) => validateField(value),
      IL_003_tiempoTrabajando: (value) => validateField(value),
      IL_004_area: (value) => validateField(value, 'IL_004_area'),
      IL_005_contactoConClientes: (value) => validateField(value),
      IL_006_puestoDeTrabajo: (value) => validateField(value),
      IL_007_participacionEnComite: (value) => validateField(value),
      IL_008_jefe: (value) => validateField(value),
      IL_009_centroDeTrabajo: (value) => validateField(value),
      SP_001_conocePoliticaDePrevencion: (value) => validateField(value),
      SP_002_haSidoCapacitado: (value) => validateField(value),
      SP_003_canalDeDenunciasExiste: (value) => validateField(value),
      SP_004_011_canalesDeDenuncias: (value) =>
        surveyRef.current?.staff_survey?.flag_012_show_sp_identified_channels ===
        false
          ? null
          : validateCheckbox(value),
      SP_012_conoceProcesoDeInvestigacion: (value) => validateField(value),
      PL_001_percepcionLideres: (value) =>
        surveyRef.current?.staff_survey?.flag_010_show_pl_001 === false
          ? null
          : validateField(value),
      TOL_001_bromasSexistas: (value) => validateField(value),
      TOL_002_bromasSobreIdentidadSexual: (value) => validateField(value),
      TOL_003_bromasSobreAparienciaFisica: (value) => validateField(value),
      TOL_004_invitarASalir: (value) => validateField(value),
      TOL_005_contactarPorRedesSociales: (value) => validateField(value),
      TOL_006_solicitarMaterialSexual: (value) => validateField(value),
      TOL_007_rozamientosIndebidos: (value) => validateField(value),
      TOL_008_tocamientosIndebidos: (value) => validateField(value),
      TOL_009_mirarConConnotacionSexual: (value) => validateField(value),
      TOL_010_invitarASalir: (value) => validateField(value),
      TOL_011_insinuarMejoresCondicionesACambioDeRelaciones: (value) =>
        validateField(value),
      TOL_012_ameazarConSuspenderContrato: (value) => validateField(value),
      CPT_001: (value) => validateField(value),
      CPT_002: (value) => validateField(value),
      CPT_003: (value) => validateField(value),
      CPT_004: (value) => validateField(value),
      AD_001_haSidoObjetoDeAcoso: (value) => validateField(value),
      AT_001_comentariosConContenidoSexual: (value) => validateField(value),
      AT_002_comentariosSobreOrientacionSexual: (value) => validateField(value),
      AT_003_comentariosSobreAparienciaFisica: (value) => validateField(value),
      AT_004_invitarASalir: (value) => validateField(value),
      AT_005_contactarPorRedesSociales: (value) => validateField(value),
      AT_006_solicitarMaterialSexual: (value) => validateField(value),
      AT_007_rozamientosIndebidos: (value) => validateField(value),
      AT_008_tocamientosIndebidos: (value) => validateField(value),
      AT_009_mirarConConnotacionSexual: (value) => validateField(value),
      AT_010_invitarASalir: (value) => validateField(value),
      AT_011_insinuarMejoresCondicionesACambioDeRelaciones: (value) =>
        validateField(value),
      AT_012_amenazarConSuspenderContrato: (value) => validateField(value),
      AD_002_013: (value) => validateCheckbox(value),
      FRE_001_frecuencia: (value) => validateField(value),
      PAD_001_002: (value) => validateCheckbox(value),
      PAD_003_007: (value) => validateCheckbox(value),
      AAD_001_008: (value) => validateCheckbox(value),
      BAD_001_010: (value) => validateCheckbox(value),
      BAD_010: (value) => {
        for (let i = 0; i < form.values.BAD_001_010.length; i++) {
          if (form.values.BAD_001_010[i] === "otro") {
            return validateField(value);
          }
        }
        return null;
      },
      COS_001_005: (value) => validateCheckbox(value),
      TD_001: (value) => validateField(value),
      TD_002_013: (value) => validateCheckbox(value),
      TT_001: (value) => validateField(value),
      TT_002: (value) => validateField(value),
      TT_003: (value) => validateField(value),
      TT_004: (value) => validateField(value),
      TT_005: (value) => validateField(value),
      TT_006: (value) => validateField(value),
      TT_007: (value) => validateField(value),
      TT_008: (value) => validateField(value),
      TT_009: (value) => validateField(value),
      TT_010: (value) => validateField(value),
      TT_011: (value) => validateField(value),
      TT_012: (value) => validateField(value),
      ACT_001_008: (value) => validateCheckbox(value),
      DANIEL_001: (value) => validateField(value),
      DANIEL_002: (value) => validateField(value),
      DANIEL_003: (value) => validateField(value),
      DANIEL_004: (value) => validateField(value),
      DANIEL_005: (value) => validateField(value),
      DANIEL_006: (value) => validateField(value),
      MARIANA_001: (value) => validateField(value),
      MARIANA_002: (value) => validateField(value),
      MARIANA_003: (value) => validateField(value),
      MARIANA_004: (value) => validateField(value),
      MARIANA_005: (value) => validateField(value),
      MARIANA_006: (value) => validateField(value),
      SANDRA_001: (value) => validateField(value),
      SANDRA_002: (value) => validateField(value),
      SANDRA_003: (value) => validateField(value),
      SANDRA_004: (value) => validateField(value),
      SANDRA_005: (value) => validateField(value),
      SANDRA_006: (value) => validateField(value),
      CLAUDIA_001: (value) => validateField(value),
      CLAUDIA_002: (value) => validateField(value),
      CLAUDIA_003: (value) => validateField(value),
      CLAUDIA_004: (value) => validateField(value),
      CLAUDIA_005: (value) => validateField(value),
      CLAUDIA_006: (value) => validateField(value),
      AD_014: (value) => validateField(value),
      CON_001: (value) => validateField(value),
      CON_003: (value) => validateField(value),
      CON_004: (value) => validateField(value),
      CON_005: (value) => validateField(value),
      CON_006: (value) => validateField(value),
      CON_007: (value) => validateField(value),
      SAT_001: (value) => validateField(value),
    },
  });

  const sendSurvey = async () => {
    setLoading(true);

    if (survey) {
      const data = transformSexualHarassmentSurveyData(
        form.values,
        survey,
        isLeader,
        esParteDeRecursosHumanos
      );

      console.table("to submit", data);

      if (mpCode && !isPreview) {
        await SurveyService.post(mpCode, data);
        localStorage.setItem(mpCode, "true");
      }
    }

    setLoading(false);
  };

  const tituloPregunta = (id: string) =>
    t("situationTitle", { id });

  const handlePrev = () => {
    embla?.scrollPrev(false);
    form.resetDirty();
  };

  const handleNext = async (submit: boolean = false) => {
    const currentSlide = embla?.slideNodes()[embla?.selectedScrollSnap() || 0];
    const isInfoSlide = currentSlide?.classList.contains("info");

    if (isInfoSlide) {
      embla?.scrollNext(false);
    } else {
      if (currentSlide) {
        const slideId = currentSlide.id.split(",");

        let validated = true;
        slideId.forEach((id) => {
          form.validateField(id);
          if (!form.isValid(id)) validated = false;
        });

        if (validated) {
          if (submit) {
            if (!isPreview) {
              try {
                await sendSurvey();
                navigate("/thank-you");
              } catch {}
            }
          } else {
            embla?.scrollNext(false);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (embla) {
      setTotalSlides(embla.slideNodes().length);
    }
  }, [form, embla]);

  const [noDenuncio, setNoDenuncio] = useState(false);

  useEffect(() => {
    const answers = form.values.AAD_001_008;
    setNoDenuncio(answers.length > 0 && !answers.includes("aad_008"));
  }, [form.values.AAD_001_008]);

  // Auto-select first area for survey ID 237 CELPSA
  useEffect(() => {
    if (survey && survey.staff_survey.id === 237 && survey.areas?.length > 0 && !form.values.IL_004_area) {
      form.setFieldValue('IL_004_area', survey.areas[0].id.toString());
    }
  }, [survey, form]);

  useEffect(() => {
    const getSurveyData = async () => {
      if (!mpCode) return;

      try {
        if (isDemoMode) {
          const language = searchParams.get("language") || "es";
          i18n.changeLanguage(language);
          ApiWithoutToken.defaults.baseURL = `${BASE_URL}${language}`;
        }

        const surveyData = await SurveyService.get(mpCode);

        if (!isDemoMode) {
          i18n.changeLanguage(surveyData.client.preferred_language);
          ApiWithoutToken.defaults.baseURL = `${BASE_URL}${surveyData.client.preferred_language}`;
        }

        updateLogo(surveyData.client.logo);
        setSurvey(surveyData);

        if (!isPreview) {
          if (surveyData.staff_survey.ended_at) {
            const fechaVencimiento = new Date(
              surveyData.staff_survey.ended_at
            );
            fechaVencimiento.setDate(fechaVencimiento.getDate() + 1);
            fechaVencimiento.setHours(fechaVencimiento.getHours() + 5);
            const fechaActual = new Date();
            if (fechaActual > fechaVencimiento) {
              setEncuestaVencida(true);
            }
          } else {
            setEncuestaTodaviaNoHaSidoLanzada(true);
          }
        }
      } catch (error) {
        console.log(error);
        setError(t("errors.link"));
      }
    };

    getSurveyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <Container size={1200}>
        <Text>{error}</Text>
      </Container>
    );
  }

  const acosoTecnicoDeterminaQueSufrioAcoso = () => {
    const checkCond = (value: string) => value === "Sí, me ha pasado.";
    return (
      checkCond(form.values.AT_001_comentariosConContenidoSexual) ||
      checkCond(form.values.AT_002_comentariosSobreOrientacionSexual) ||
      checkCond(form.values.AT_003_comentariosSobreAparienciaFisica) ||
      checkCond(form.values.AT_004_invitarASalir) ||
      checkCond(form.values.AT_005_contactarPorRedesSociales) ||
      checkCond(form.values.AT_006_solicitarMaterialSexual) ||
      checkCond(form.values.AT_007_rozamientosIndebidos) ||
      checkCond(form.values.AT_008_tocamientosIndebidos) ||
      checkCond(form.values.AT_009_mirarConConnotacionSexual) ||
      checkCond(form.values.AT_010_invitarASalir) ||
      checkCond(
        form.values.AT_011_insinuarMejoresCondicionesACambioDeRelaciones
      ) ||
      checkCond(form.values.AT_012_amenazarConSuspenderContrato)
    );
  };

  const testigoTecnicoDeterminaQueFueTestigoDeAcoso = () => {
    const checkCond = (value: string) => value === "Sí, he sido testigo.";
    return (
      checkCond(form.values.TT_001) ||
      checkCond(form.values.TT_002) ||
      checkCond(form.values.TT_003) ||
      checkCond(form.values.TT_004) ||
      checkCond(form.values.TT_005) ||
      checkCond(form.values.TT_006) ||
      checkCond(form.values.TT_007) ||
      checkCond(form.values.TT_008) ||
      checkCond(form.values.TT_009) ||
      checkCond(form.values.TT_010) ||
      checkCond(form.values.TT_011) ||
      checkCond(form.values.TT_012)
    );
  };

  if (!mpCode) {
    return (
      <Container size={1200}>
        <Text>El enlace no es válido.</Text>
      </Container>
    );
  }

  if (survey && !encuestaVencida && !encuestaTodaviaNoHaSidoLanzada) {
    if (isDemoMode || !localStorage.getItem(mpCode || "")) {
      return (
        <Container size={1200}>
          {isDemoMode && (
            <Alert color="yellow" radius="md" mb="md">
              <Text weight={600}>{t("demoBanner.title")}</Text>
              <Text size="sm">{t("demoBanner.text")}</Text>
            </Alert>
          )}
          <form>
            <Carousel
              speed={90}
              mx="auto"
              getEmblaApi={setEmbla}
              withControls={false}
              draggable={false}
              withKeyboardEvents={false}
            >
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyWelcome1 />
              </Carousel.Slide>
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyWelcome2 />
              </Carousel.Slide>
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyAcknowledgement />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="IP_001_genero"
              >
                <Genero form={form} staff_survey={survey} />
              </Carousel.Slide>
              {survey.staff_survey.flag_002 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IP_002_edad"
                >
                  <Edad form={form} />
                </Carousel.Slide>
              )}
              {survey.staff_survey.flag_003 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IP_003_comunidad"
                >
                  <Comunidad form={form} />
                </Carousel.Slide>
              )}
              {survey.staff_survey.flag_004 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IP_004_etnia"
                >
                  <Etnia form={form} />
                </Carousel.Slide>
              )}
              {survey.staff_survey.flag_011_show_il_001 &&
                survey.staff_survey.contract_types &&
                survey.staff_survey.contract_types.length > 0 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IL_001_contrato"
                >
                  <Contrato form={form} contractTypes={survey.staff_survey.contract_types} />
                </Carousel.Slide>
              )}
              {survey.staff_survey.flag_005 && survey.work_shifts && survey.work_shifts.length > 0 && survey.work_shifts.some(shift => shift.description?.trim()) && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IL_002_turno"
                >
                  <Turno form={form} turnos={survey.work_shifts} />
                </Carousel.Slide>
              )}
              {survey.staff_survey.flag_006 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IL_003_tiempoTrabajando"
                >
                  <TiempoTrabajando form={form} />
                </Carousel.Slide>
              )}
              {survey.staff_survey.flag_007 && survey.areas && survey.areas.length > 0 && survey.areas.some(area => area.description?.trim()) && survey.staff_survey.id !== 237 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IL_004_area"
                >
                  <Area form={form} areas={survey.areas} />
                </Carousel.Slide>
              )}
              {/* TODO: remove mpCode BID Colombia, Costa Rica, Honduras, Panama and Guatemala hardCoded after confirmation */}
              {survey.staff_survey.id !== 225 && survey.staff_survey.id !== 382 && survey.staff_survey.id !== 383 && survey.staff_survey.id !== 384 && survey.staff_survey.id !== 397 && (
              <Carousel.Slide
                className={classes.carouselSlide}
                id="IL_005_contactoConClientes"
              >
                <ContactoConClientes form={form} />
              </Carousel.Slide>
              )}
              {survey.jobs && survey.jobs.length > 0 && survey.jobs.some(job => job.description?.trim()) && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IL_006_puestoDeTrabajo"
                >
                  <PuestoDeTrabajo
                    form={form}
                    puestos={survey.jobs}
                    setIsLeader={setIsLeader}
                  />
                </Carousel.Slide>
              )}
              {/* TODO: remove mpCode BID Colombia, Costa Rica, Honduras, Panama and Guatemala hardCoded after confirmation */}
              {survey.staff_survey.id !== 225 && survey.staff_survey.id !== 382 && survey.staff_survey.id !== 383 && survey.staff_survey.id !== 384 && survey.staff_survey.id !== 397 && (
              <Carousel.Slide
                className={classes.carouselSlide}
                id="IL_007_participacionEnComite"
              >
                <ParticipacionEnComite
                  form={form}
                  setParteDeRecursosHumanos={setParteDeRecursosHumanos}
                />
              </Carousel.Slide>
              )}
              {/* TODO: remove mpCode BID Colombia, Costa Rica, Honduras, Panama and Guatemala hardCoded after confirmation */}
              {survey.staff_survey.id !== 225 && survey.staff_survey.id !== 382 && survey.staff_survey.id !== 383 && survey.staff_survey.id !== 384 && survey.staff_survey.id !== 397 && (
              <Carousel.Slide
                className={classes.carouselSlide}
                id="IL_008_jefe"
              >
                <Jefe form={form} />
              </Carousel.Slide>
              )}
              {survey.staff_survey.flag_009 && survey.workplaces && survey.workplaces.length > 0 && survey.workplaces.some(workplace => workplace.description?.trim()) && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="IL_009_centroDeTrabajo"
                >
                  <CentroDeTrabajo form={form} centros={survey.workplaces} />
                </Carousel.Slide>
              )}
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyInfoPolitics />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="SP_001_conocePoliticaDePrevencion"
              >
                <ConocePoliticaDePrevencion form={form} />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="SP_002_haSidoCapacitado"
              >
                <HaSidoCapacitado form={form} />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="SP_003_canalDeDenunciasExiste"
              >
                <CanalDeDenunciasExiste form={form} />
              </Carousel.Slide>
              {survey.staff_survey.flag_012_show_sp_identified_channels !==
                false && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="SP_004_011_canalesDeDenuncias"
                >
                  <CanalesDeDenuncias
                    form={form}
                    channels={
                      survey.staff_survey.identified_report_channels ?? []
                    }
                  />
                </Carousel.Slide>
              )}
              <Carousel.Slide
                className={classes.carouselSlide}
                id="SP_012_conoceProcesoDeInvestigacion"
              >
                <ConoceProcesoDeInvestigacion form={form} />
              </Carousel.Slide>
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyInfoSituations />
              </Carousel.Slide>
              {isLeader && survey.staff_survey.flag_010_show_pl_001 !== false && survey.staff_survey.id !== 225 && survey.staff_survey.id !== 382 && survey.staff_survey.id !== 383 && survey.staff_survey.id !== 384 && survey.staff_survey.id !== 397 && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="PL_001_percepcionLideres"
                >
                  <PercepcionLideres form={form} />
                </Carousel.Slide>
              )}
              <Carousel.Slide
                className={classes.carouselSlide}
                id="TOL_001_bromasSexistas,TOL_002_bromasSobreIdentidadSexual,TOL_003_bromasSobreAparienciaFisica,TOL_004_invitarASalir,TOL_005_contactarPorRedesSociales,TOL_006_solicitarMaterialSexual,TOL_007_rozamientosIndebidos,TOL_008_tocamientosIndebidos,TOL_009_mirarConConnotacionSexual,TOL_010_invitarASalir,TOL_011_insinuarMejoresCondicionesACambioDeRelaciones,TOL_012_ameazarConSuspenderContrato"
              >
                <Tolerancia form={form} />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="CPT_001,CPT_002,CPT_003,CPT_004"
              >
                <Conceptos form={form} />
              </Carousel.Slide>
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyInfoPersonalExperience />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="AD_001_haSidoObjetoDeAcoso"
              >
                <HaSidoObjetoDeAcoso
                  form={form}
                  setHaSidoObjetoDeAcoso={setHaSidoObjetoDeAcoso}
                />
              </Carousel.Slide>
              {haSidoObjetoDeAcoso && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="AD_002_013"
                >
                  <PreguntasAcosoDeclarado form={form} />
                </Carousel.Slide>
              )}
              {haSidoObjetoDeAcoso && (
                <>
                  <Carousel.Slide
                    className={classes.carouselSlide}
                    id="PAD_001_002"
                  >
                    <GeneroAcosador form={form} />
                  </Carousel.Slide>
                  <Carousel.Slide
                    className={classes.carouselSlide}
                    id="PAD_003_007"
                  >
                    <PreguntasPerfilAcosador form={form} />
                  </Carousel.Slide>
                </>
              )}
              {haSidoObjetoDeAcoso && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="AAD_001_008"
                >
                  <AccionesAcosoDeclarado form={form} />
                </Carousel.Slide>
              )}
              {haSidoObjetoDeAcoso && noDenuncio && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="BAD_001_010,BAD_010"
                >
                  <BarrerasDeDenuncia form={form} />
                </Carousel.Slide>
              )}
              {!haSidoObjetoDeAcoso && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="AT_001_comentariosConContenidoSexual,AT_002_comentariosSobreOrientacionSexual,AT_003_comentariosSobreAparienciaFisica,AT_004_invitarASalir,AT_005_contactarPorRedesSociales,AT_006_solicitarMaterialSexual,AT_007_rozamientosIndebidos,AT_008_tocamientosIndebidos,AT_009_mirarConConnotacionSexual,AT_010_invitarASalir,AT_011_insinuarMejoresCondicionesACambioDeRelaciones,AT_012_amenazarConSuspenderContrato"
                >
                  <PreguntasAcosoTecnico form={form} />
                </Carousel.Slide>
              )}
              {(haSidoObjetoDeAcoso ||
                acosoTecnicoDeterminaQueSufrioAcoso()) && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="FRE_001_frecuencia"
                >
                  <Frecuencia form={form} />
                </Carousel.Slide>
              )}
              {(haSidoObjetoDeAcoso ||
                acosoTecnicoDeterminaQueSufrioAcoso()) && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="COS_001_005"
                >
                  <Costos form={form} />
                </Carousel.Slide>
              )}
              <Carousel.Slide className={classes.carouselSlide} id="TD_001">
                <HaSidoTestigoDeAcoso
                  form={form}
                  setHaSidoTestigoDeAcoso={setHaSidoTestigoDeAcoso}
                />
              </Carousel.Slide>
              {haSidoTestigoDeAcoso && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="TD_002_013"
                >
                  <PreguntasTestigosDeclarados form={form} />
                </Carousel.Slide>
              )}
              {!haSidoTestigoDeAcoso && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="TT_001,TT_002,TT_003,TT_004,TT_005,TT_006,TT_007,TT_008,TT_009,TT_010,TT_011,TT_012"
                >
                  <PreguntasTestigosTecnicos form={form} />
                </Carousel.Slide>
              )}
              {(haSidoTestigoDeAcoso ||
                testigoTecnicoDeterminaQueFueTestigoDeAcoso()) && (
                <Carousel.Slide
                  className={classes.carouselSlide}
                  id="ACT_001_008"
                >
                  <PreguntasAccionesTestigos form={form} />
                </Carousel.Slide>
              )}
              {(isLeader || esParteDeRecursosHumanos) && (
                <>
                  <Carousel.Slide className={`${classes.carouselSlide} info`}>
                    <SurveyInfoCasos />
                  </Carousel.Slide>
                  <Carousel.Slide
                    className={classes.carouselSlide}
                    id="DANIEL_001,DANIEL_002,DANIEL_003,DANIEL_004,DANIEL_005,DANIEL_006"
                  >
                    <CasoTemplate
                      form={form}
                      caso={survey.cases.first}
                      title={tituloPregunta("1")}
                      questionIds={[
                        "DANIEL_001",
                        "DANIEL_002",
                        "DANIEL_003",
                        "DANIEL_004",
                        "DANIEL_005",
                        "DANIEL_006",
                      ]}
                    />
                  </Carousel.Slide>
                  <Carousel.Slide
                    className={classes.carouselSlide}
                    id="MARIANA_001,MARIANA_002,MARIANA_003,MARIANA_004,MARIANA_005,MARIANA_006"
                  >
                    <CasoTemplate
                      form={form}
                      caso={survey.cases.second}
                      title={tituloPregunta("2")}
                      questionIds={[
                        "MARIANA_001",
                        "MARIANA_002",
                        "MARIANA_003",
                        "MARIANA_004",
                        "MARIANA_005",
                        "MARIANA_006",
                      ]}
                    />
                  </Carousel.Slide>
                  <Carousel.Slide
                    className={classes.carouselSlide}
                    id="SANDRA_001,SANDRA_002,SANDRA_003,SANDRA_004,SANDRA_005,SANDRA_006"
                  >
                    <CasoTemplate
                      form={form}
                      caso={survey.cases.third}
                      title={tituloPregunta("3")}
                      questionIds={[
                        "SANDRA_001",
                        "SANDRA_002",
                        "SANDRA_003",
                        "SANDRA_004",
                        "SANDRA_005",
                        "SANDRA_006",
                      ]}
                    />
                  </Carousel.Slide>
                  <Carousel.Slide
                    className={classes.carouselSlide}
                    id="CLAUDIA_001,CLAUDIA_002,CLAUDIA_003,CLAUDIA_004,CLAUDIA_005,CLAUDIA_006"
                  >
                    <CasoTemplate
                      form={form}
                      caso={survey.cases.fourth}
                      title={tituloPregunta("4")}
                      questionIds={[
                        "CLAUDIA_001",
                        "CLAUDIA_002",
                        "CLAUDIA_003",
                        "CLAUDIA_004",
                        "CLAUDIA_005",
                        "CLAUDIA_006",
                      ]}
                    />
                  </Carousel.Slide>
                </>
              )}
              <Carousel.Slide className={classes.carouselSlide} id="AD_014">
                <HaSidoObjetoDeAcosoAlgunaVezEnSuVida form={form} />
              </Carousel.Slide>
              <Carousel.Slide className={`${classes.carouselSlide} info`}>
                <SurveyInfoTratamientoDeCasos />
              </Carousel.Slide>
              <Carousel.Slide
                className={classes.carouselSlide}
                id="CON_001,CON_003,CON_004,CON_005,CON_006,CON_007"
              >
                <PreguntasConfianzaEnLaOrganizacion form={form} />
              </Carousel.Slide>
              {/* TODO: remove mpCode BID Colombia, Costa Rica, Honduras, Panama and Guatemala hardCoded after confirmation */}
              {survey.staff_survey.id !== 225 && survey.staff_survey.id !== 382 && survey.staff_survey.id !== 383 && survey.staff_survey.id !== 384 && survey.staff_survey.id !== 397 && (
              <Carousel.Slide className={classes.carouselSlide} id="SAT_001">
                <PreguntaSatisfaccion form={form} />
              </Carousel.Slide>
              )}
            </Carousel>
          </form>
          <Progress
            value={scrollProgress}
            styles={{
              bar: { transitionDuration: "0ms" },
              root: { maxWidth: "100%" },
            }}
            size="sm"
            mt="xl"
            mx="auto"
            color="#d92020"
          />
          <Group position="apart">
            <Text color="dimmed" size="xs">
              <>
                {t("page")} {(embla?.selectedScrollSnap() || 0) + 1} {t("of")}{" "}
                {totalSlides}
              </>
            </Text>
            <SurveyNavigationButtons
              embla={embla}
              handlePrev={handlePrev}
              handleNext={handleNext}
              disabled={!survey || loading}
            />
          </Group>
        </Container>
      );
    } else {
      return (
        <Container size={1200}>
          Ya has completado la encuesta. ¡Gracias por tu tiempo!
        </Container>
      );
    }
  } else if (encuestaVencida) {
    return (
      <Container size={1200}>
        <Title order={3}>¡La encuesta ya finalizó!</Title>
        <Text>Este link no se encuentra disponible.</Text>
      </Container>
    );
  } else if (encuestaTodaviaNoHaSidoLanzada) {
    return (
      <Container size={1200}>
        <Title order={3}>¡La encuesta todavía no ha sido lanzada!</Title>
        <Text>Este link no se encuentra disponible.</Text>
      </Container>
    );
  } else {
    return (
      <Container size={1200} h={550}>
        <Flex justify="center" align="center" h="100%">
          <Loader color="#d92020" />
        </Flex>
      </Container>
    );
  }
};
